import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle,
  Download,
  ImageDown,
  MessageCircleQuestion,
  SquareCheck,
  Trash,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/dropdown-menu/dropdown-menu";
// componentes
import { DynamicTable } from "@components/table-custom/table";

import { MyTasksObservationModal } from "@pages/admin/remessas/components/modal-adicionar-obs-tarefa/modal-adicionar-obs-tarefa";
import { RecusarTarefaModal } from "@pages/admin/remessas/components/recusar-tarefa-modal";
import { SearchInputMinhasTarefas } from "@pages/admin/remessas/components/search-minhas-tarefas";
// helpers
import { FinalizarTarefaModal } from "@pages/producao/components/finalizar-tarefa-modal";

import { useMinhasTarefasAdmin } from "@/hooks/admin/minhas-tarefas";
import { useUpdateTarefa } from "@/hooks/minhas-tarefas/use-update-tarefa";
import { useDownloadRemessaPlanilha } from "@/hooks/remessas/get-download-planilha-remessa";
import { useDownloadRemessaZip } from "@/hooks/remessas/get-download-zip-remessa";

// hooks / zustand
import { useStoreZustandMinhasTarefas } from "@/store-zustand/admin/minhas-tarefas";
import { useStoreZustandPegarTarefas } from "@/store-zustand/admin/pegar-tarefas";

import { cn } from "@/data/lib/tailwind-merge";
import { getSituacaoColorBySlug } from "@/utils/get-situacao-color-class";
import { getStatusLabel } from "@/utils/get-status-label";
import { isDownloadDadosDisabled, isDownloadFotosDisabled } from "@/utils/remessa-download-rules";

type IMinhasTarefas = {
  setCurrentScreen: () => void;
};
function removerAcentos(texto: string): string {
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

const etapas = ["pendente", "em_producao", "pronto_para_imprimir", "em_aprovacao"];

export function MinhasTarefasTable({ setCurrentScreen }: IMinhasTarefas) {
  const router = useNavigate();

  const queryClient = useQueryClient(); // ← necessário para invalidar a query
  const [isModal, setIsModal] = useState(false);
  const [numeroRemessa, setNumeroRemessa] = useState(0);

  const [isModalRecusarTarefa, setIsModalRecusarTarefa] = useState(false);

  const { mutate: mutateUseUpdateTarefa } = useUpdateTarefa();
  const {
    currentPage,
    setCurrentPage,
    totalItemsPage,
    itemsPerPage,
    lastPage,
    idRemessa,
    setIdRemessa,
    isModalFinalizarTarefa,
    setIsModalFinalizarTarefa,
    setTarefa: setTarefaMinhasTarefas,
  } = useStoreZustandMinhasTarefas();

  const { setTarefa } = useStoreZustandPegarTarefas();

  const { minhasTarefas, paginations, total_remessas_em_producao, isLoading } =
    useMinhasTarefasAdmin();
  const { downloadZip } = useDownloadRemessaZip();
  const { downloadPlanilha } = useDownloadRemessaPlanilha();

  const handleToogleModal = useCallback(() => {
    setIsModal(!isModal);
  }, [isModal]);

  const handleToogleModalFinalizarTarefa = useCallback(() => {
    setIsModalFinalizarTarefa(!isModalFinalizarTarefa);
  }, [isModalFinalizarTarefa, setIsModalFinalizarTarefa]);

  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? Math.min(lastPage, currentPage + 1)
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, lastPage, setCurrentPage]
  );

  const handleEtapaChange = (item: any, etapa: string) => {
    console.log(`Remessa ${item.remessa} alterada para: ${removerAcentos(etapa)}`);

    mutateUseUpdateTarefa(
      {
        id_remessa: Number(item.remessa),
        payload: {
          situacao: removerAcentos(etapa),
        },
      },
      {
        onSuccess: async () => {
          setCurrentScreen();
          await queryClient.invalidateQueries({
            queryKey: ["minhas-tarefas-admin"],
          });
        },
        onError: (error) => {
          console.error("Erro ao atualizar tarefa:", error);
        },
      }
    );

    // Aqui você pode abrir modal, chamar API ou atualizar estado
  };

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      <DynamicTable
        fillViewport
        title="Minhas Tarefas"
        description="Gerencie suas tarefas e visualize quando quiser."
        subDescripion={`Total de ${total_remessas_em_producao} resultados`}
        isLoadingPage={isLoading}
        data={minhasTarefas || []}
        totalItems={totalItemsPage}
        itemsPerPage={itemsPerPage}
        onGoToPage={onGoToPage}
        columns={[
          { label: "Remessa", accessor: "numero_remessa" },
          { label: "Cliente", accessor: "nome_identificacao", limit: 60 },
          {
            label: "Modelo",
            accessor: "modelo",
            limit: 60,
            customRender: (value: any, item: any) => (
              <Link
                to={`/visualizar-modelo`}
                className="font-medium text-blue-600 transition-colors duration-200 hover:text-blue-800 hover:underline"
                onClick={() => setTarefa(item)}
              >
                {value}
              </Link>
            ),
          },
          { label: "Qtd", accessor: "quantidadeSolicitacao" },
          { label: "Tecnologia", accessor: "tecnologia" },
          { label: "Furo", accessor: "furo" },
          {
            label: "Posição",
            accessor: "posicao",
          },
          {
            label: "Prazo",
            accessor: "prazo",
            isBadge: true,
            badgeClassName: "bg-green-500",
          },
          {
            label: "Situacao",
            accessor: "situacao",
            customRender: (value: string, item: any) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition hover:opacity-90",
                      getSituacaoColorBySlug(value)
                    )}
                    title="Clique para alterar etapa"
                  >
                    <span className="text-xs">{getStatusLabel(value)}</span>
                    <CheckCircle className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent className="w-56">
                  {etapas.map((etapa, index) => (
                    <DropdownMenuItem
                      key={etapa + index}
                      onSelect={() => handleEtapaChange(item, etapa)}
                      className={cn(
                        "cursor-pointer capitalize",
                        etapa === value && cn("font-semibold", getSituacaoColorBySlug(etapa))
                      )}
                    >
                      {getStatusLabel(etapa)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
          { label: "Solicitante", accessor: "solicitante", limit: 60 },
          { label: "Designer", accessor: "designer", limit: 60 },
          { label: "Data Solicitação", accessor: "data" },
        ]}
        paginations={paginations}
        currentPage={currentPage}
        lastPage={lastPage}
        actions={[
          {
            icon: Download,
            onClick: async (item: any) => {
              downloadPlanilha(item);
            },

            disabled: (item: any) => isDownloadDadosDisabled(item),
            label: "Baixar dados",
            textColor: "text-blue-600",
          },
          {
            icon: ImageDown,
            disabled: (item: any) => isDownloadFotosDisabled(item),
            label: "Baixar fotos",
            textColor: "text-blue-600",
            onClick: (item: any) => {
              downloadZip({
                remessa_id: item.remessa,
                numero_remessa: item.numero_remessa,
              });
            },
          },
          {
            icon: SquareCheck,
            label: "Finalizar tarefa",
            iconClassName: "h-4 w-4 text-green-600 bg-green-100",
            onClick: (item: any) => {
              setIdRemessa(item?.remessa);
              setNumeroRemessa(item?.numero_remessa);
              setTarefa(item);
              setIsModalFinalizarTarefa(true);
            },
          },
          {
            icon: MessageCircleQuestion,
            label: "Observação",
            iconClassName: (item: any) =>
              item.hasMensagem ? "h-4 w-4 text-yellow-500" : "h-4 w-4 text-zinc-900",
            onClick: (item: any) => {
              setIdRemessa(item.remessa);
              setTarefaMinhasTarefas(item);
              setIsModal(true);
            },
          },
          {
            icon: Trash,
            label: "Excluir",
            textColor: "text-red-600",
            onClick: (item: any) => {
              setIdRemessa(item?.remessa);
              setTarefa(item);
              setIsModalRecusarTarefa(!isModalRecusarTarefa);
            },
          },
        ]}
        childerSearch={<SearchInputMinhasTarefas />}
      />

      {isModal && (
        <MyTasksObservationModal
          idRemessa={idRemessa!}
          handleToggleCreateActvityModal={handleToogleModal}
          setCurrentScreen={setCurrentScreen}
        />
      )}

      {isModalFinalizarTarefa && idRemessa && (
        <FinalizarTarefaModal
          idRemessa={idRemessa}
          numero_remessa={numeroRemessa}
          handleToggleCreateActvityModal={handleToogleModalFinalizarTarefa}
          setCurrentScreen={setCurrentScreen}
        />
      )}
      {isModalRecusarTarefa && (
        <RecusarTarefaModal
          idRemessa={idRemessa!}
          handleToggleCreateActvityModal={() => {
            setIsModalRecusarTarefa(!isModalRecusarTarefa);
          }}
          setCurrentScreen={() => {
            router("/search-shipment");
          }}
        />
      )}
    </div>
  );
}
